import {
  alpha,
  Tab as MuiTab,
  Tabs as MuiTabs,
  Stack,
  styled,
  tabClasses,
  tabsClasses,
} from '@mui/material';
import { GLOBAL_TAB_LABEL_CLASSES } from 'src/components/GlobalTabs/tab-label-classes';

export const GLOBAL_TABS_HEIGHT = '3rem';
// GLOBAL_TABS_INNER_HEIGHT should be a bit smaller than GLOBAL_TABS_HEIGHT.
export const GLOBAL_TABS_INNER_HEIGHT = '2.125rem';

// The close button overlays the label rather than reserving space for it, so the label fades out
// underneath instead of being chopped mid-letter. Reserving a slot would have cost every tab the
// width whether or not anything was ever drawn there.
const TRAILING_FADE = `linear-gradient(to right, #000 calc(100% - 1.625rem), transparent calc(100% - 0.25rem))`;

export const GlobalTabsBar = styled(Stack)({
  flexDirection: 'row',
  flex: '0 0 auto',
  margin: '0.25rem 0.25rem 0 0',
  padding: '0.25rem 0.375rem 0 0',
});

export const GlobalTabsContainer = styled(MuiTabs)(({ theme }) => {
  const { sys } = theme.palette.custom.OV;

  return {
    maxHeight: GLOBAL_TABS_INNER_HEIGHT,
    minHeight: GLOBAL_TABS_INNER_HEIGHT,
    height: GLOBAL_TABS_INNER_HEIGHT,
    width: '100%',
    [`& .${tabsClasses.indicator}`]: {
      display: 'none',
    },
    [`& .${tabsClasses.flexContainer}`]: {
      alignItems: 'stretch',
      gap: '0.125rem',
    },
    [`& .${tabsClasses.scroller}`]: {
      scrollbarWidth: 'none',
    },
    [`& .${tabsClasses.scroller}::-webkit-scrollbar`]: {
      display: 'none',
    },
    [`& .${tabsClasses.scrollButtons}`]: {
      width: GLOBAL_TABS_INNER_HEIGHT,
      height: GLOBAL_TABS_INNER_HEIGHT,
      flexShrink: 0,
      marginInline: '0.125rem',
      borderRadius: '0.5rem',
      border: `1px solid ${sys.outlineVariant}`,
      backgroundColor: sys.surfaceContainerLow,
      color: sys.onSurface,
      opacity: 1,
      transition: theme.transitions.create(['background-color', 'color'], {
        duration: 180,
      }),
      '&:hover': {
        backgroundColor: sys.surfaceContainer,
      },
      '&.Mui-disabled': {
        opacity: 1,
        color: sys.outline,
      },
    },
  };
});

export const Tab = styled(MuiTab)(({ theme }) => {
  const { sys } = theme.palette.custom.OV;

  return {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.375rem',
    textTransform: 'none',
    // Trailing pad stays small because the close button overlays the label (see TRAILING_FADE).
    padding: '0.1875rem 0.5625rem 0.1875rem 0.6875rem',
    maxHeight: GLOBAL_TABS_INNER_HEIGHT,
    minHeight: GLOBAL_TABS_INNER_HEIGHT,
    height: GLOBAL_TABS_INNER_HEIGHT,
    // Every tab shows its title now, so tabs need a floor wide enough for one to be worth
    // reading and a ceiling so a long subject cannot crowd out its neighbours. Between the two
    // they size to content; past the ceiling the strip scrolls rather than squashing anything.
    minWidth: '8.25rem',
    width: 'auto',
    maxWidth: '11.125rem',
    flexShrink: 0,
    overflow: 'hidden',
    borderRadius: '0.5rem',
    border: `1px solid ${sys.outlineVariant}`,
    backgroundColor: sys.surfaceContainerLow,
    color: sys.onSurface,
    transition: theme.transitions.create(
      ['background-color', 'border-color', 'box-shadow', 'color'],
      { duration: 180 },
    ),

    [`&:not(.${tabClasses.selected}):hover`]: {
      backgroundColor: sys.surfaceContainer,
    },

    // Only while the close button is actually drawn over the label, so a tab at rest keeps a
    // hard-edged ellipsis and pays nothing for a slot it is not using.
    [`&:hover .${GLOBAL_TAB_LABEL_CLASSES.content}`]: {
      WebkitMaskImage: TRAILING_FADE,
      maskImage: TRAILING_FADE,
    },

    [`&.${tabClasses.selected}`]: {
      backgroundColor: sys.surfaceContainerHigh,
      color: sys.onBackground,
      boxShadow: `0 1px 2px ${alpha(sys.shadow, theme.palette.mode === 'dark' ? 0.18 : 0.05)}`,
      [`.${GLOBAL_TAB_LABEL_CLASSES.title}`]: {
        fontWeight: 600,
      },
      [`.${GLOBAL_TAB_LABEL_CLASSES.overline}`]: {
        color: sys.onSurface,
      },
    },

    '.close-tab-button': {
      position: 'absolute',
      top: '50%',
      right: '0.3125rem',
      transform: 'translateY(-50%)',
      width: '1.25rem',
      height: '1.25rem',
      padding: 0,
      borderRadius: '0.25rem',
      color: sys.onSurfaceVariant,
      opacity: 0,
      pointerEvents: 'none',
      transition: theme.transitions.create(['opacity', 'background-color'], {
        duration: 120,
      }),
    },
    '&:hover .close-tab-button': {
      opacity: 1,
      pointerEvents: 'auto',
    },
    '.close-tab-button:hover': {
      backgroundColor: sys.surfaceContainerHighest,
      color: sys.onBackground,
    },
  };
});
